import { StandardStatus } from '@zoocasa/go-search';
import { ListingDisplayPolicy } from './display-policy';
import { isSold } from 'types/reso';

/**
 * Value object that encapsulates how to present a listing's price.
 * 
 * Determines what price to show, how to format it, and what layout to use
 * based on the listing's status and regional display policies.
 */
export class PricePresentation {
  readonly #isSold: boolean;
  readonly #shouldShowSoldPrice: boolean;
  readonly #displaySoldPrice: string;
  readonly #displayListPrice: string;
  readonly #hasListPrice: boolean;

  private constructor(
    isSold: boolean,
    shouldShowSoldPrice: boolean,
    displaySoldPrice: string,
    displayListPrice: string,
    hasListPrice: boolean
  ) {
    this.#isSold = isSold;
    this.#shouldShowSoldPrice = shouldShowSoldPrice;
    this.#displaySoldPrice = displaySoldPrice;
    this.#displayListPrice = displayListPrice;
    this.#hasListPrice = hasListPrice;
  }

  /**
   * Creates a PricePresentation from listing data.
   */
  static fromListing(
    standardStatus: StandardStatus,
    price: number | null,
    soldPrice: number | null,
    displayPolicy: ListingDisplayPolicy,
    formatCurrency: (value: number) => string
  ): PricePresentation {
    const isListingSold = isSold(standardStatus);
    const shouldShowSoldPrice = displayPolicy.shouldShowSoldPrice(soldPrice);
    const displaySoldPrice = displayPolicy.getDisplayableSoldPrice(soldPrice, formatCurrency);
    const displayListPrice = displayPolicy.getDisplayablePrice(price, formatCurrency);
    const hasListPrice = price !== null && price > 0;

    return new PricePresentation(
      isListingSold,
      shouldShowSoldPrice,
      displaySoldPrice,
      displayListPrice,
      hasListPrice
    );
  }

  /**
   * Whether to show the sold price section.
   */
  get showSoldPriceSection(): boolean {
    return this.#isSold && this.#shouldShowSoldPrice;
  }

  /**
   * Whether to show the list price section.
   */
  get showListPriceSection(): boolean {
    return !this.#isSold || !this.#shouldShowSoldPrice;
  }

  /**
   * Whether this is a sold listing without a displayable sold price.
   */
  get isSoldWithoutSoldPrice(): boolean {
    return this.#isSold && !this.#shouldShowSoldPrice;
  }

  /**
   * Whether to show the "List: " prefix before the list price.
   * Only show prefix when there's an actual list price (not a placeholder).
   */
  get shouldShowListPrefix(): boolean {
    return this.isSoldWithoutSoldPrice && this.#hasListPrice;
  }

  /**
   * The formatted sold price to display.
   */
  get soldPriceDisplay(): string {
    return this.#displaySoldPrice;
  }

  /**
   * The formatted list price to display.
   */
  get listPriceDisplay(): string {
    return this.#displayListPrice;
  }

  /**
   * Whether to show the original list price alongside the sold price.
   */
  get showOriginalListPrice(): boolean {
    return this.#hasListPrice;
  }
}
